import React, { useMemo } from 'react'
import { Link } from 'react-router-dom'
import { Box, Chip, styled, Typography } from '@the-platform-group/component-library'
import { DataGridActionCell } from '@the-platform-group/component-library/DataGrid'
import { Delete as DeleteIcon } from '@the-platform-group/component-library/Icons'

import { usePromptMetadata } from 'hooks/usePromptMetadata'

const StyledBox = styled(Box)(() => ({ display: 'flex', flexDirection: 'column' }))

const renderUpdatedAtCell = ({ value }) => {
  if (!value) {
    return null
  }
  return <Typography variant="body2">{new Date(value).toLocaleString()}</Typography>
}

/**
 * Builds memoized DataGrid column definitions for the agentic prompts list.
 * @param {object} handlers - row action callbacks
 * @param {function} handlers.onDelete - called with the prompt row when the delete action is clicked
 * @returns {Array<object>} DataGrid column definitions
 */
const useColumnConfig = ({ onDelete }) => {
  const { getWorkflowLabel, getAppDomainLabel } = usePromptMetadata()

  return useMemo(
    () => [
      {
        field: 'title',
        headerName: 'Title',
        flex: 2,
        sortable: false,
        renderCell: ({ id, value }) => (
          <StyledBox>
            <Link to={`/ai-prompts/${id}`}>{value}</Link>
            <Typography variant="body2" component="span" color="text.secondary">
              {id}
            </Typography>
          </StyledBox>
        ),
      },
      {
        field: 'workflow',
        headerName: 'Workflow',
        flex: 1.5,
        sortable: false,
        renderCell: ({ value }) => (
          <Typography variant="body2" noWrap>
            {getWorkflowLabel(value)}
          </Typography>
        ),
      },
      {
        field: 'categories',
        headerName: 'App Domains',
        flex: 1.5,
        sortable: false,
        renderCell: ({ value }) => {
          if (!value?.length) {
            return null
          }

          return (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {value.map(category => (
                <Chip key={category} label={getAppDomainLabel(category)} size="small" />
              ))}
            </Box>
          )
        },
      },
      {
        field: 'parentPromptTitle',
        headerName: 'Parent',
        flex: 1,
        sortable: false,
        renderCell: ({ value }) =>
          value ? (
            <Typography variant="body2" noWrap>
              {value}
            </Typography>
          ) : null,
      },
      {
        field: 'status',
        headerName: 'Status',
        flex: 0.5,
        sortable: false,
      },
      {
        field: 'updatedAt',
        headerName: 'Updated',
        flex: 1,
        sortable: false,
        renderCell: renderUpdatedAtCell,
      },
      {
        field: 'actions',
        type: 'actions',
        align: 'right',
        flex: 0.5,
        sortable: false,
        getActions: ({ row }) => [
          <DataGridActionCell
            key="delete"
            icon={<DeleteIcon />}
            title="Delete prompt"
            onClick={() => onDelete(row)}
          />,
        ],
      },
    ],
    [onDelete, getWorkflowLabel, getAppDomainLabel],
  )
}

export default useColumnConfig
